// The table's combat rules (issue 1305): the DM's choice, in Properties > Scene, between this system's rules — "(none)"
// when it has none — and another system's, from the systems the site lets this DM read (asked of the table's own
// server, which asks the site with the DM's bearer; the browser never calls the site). A borrow is the table's rules
// setting (`table.rules`), checked by the server as it is committed; a player is never shown this control, and the
// server refuses their change.
import { useEffect, useState } from 'react';
import { parseBorrow, type Borrow } from '@dragons-table/vtt-shared';
import { requestJson } from '../../api';
import { useTableState } from '../../table/TableState';
import { useHolds } from '../../table/mayAct';
import { useShell } from '../context';

export interface SystemChoices {
  own: { slug: string; name: string | null; combat: boolean };
  /** Null when the site cannot say now. */
  systems: Array<{ slug: string; name: string }> | null;
  borrow: Borrow | null;
}

/** The DM's choices, or null when they cannot be read now (a player is answered 403). */
export async function fetchSystemChoices(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<SystemChoices | null> {
  const { status, body } = await requestJson(`/api/tables/${encodeURIComponent(campaignId)}/systems`, {}, fetchImpl);
  const b = body as { own?: { slug?: unknown; name?: unknown; combat?: unknown }; systems?: unknown; borrow?: unknown } | null;
  if (status !== 200 || !b || !b.own || typeof b.own.slug !== 'string') return null;
  const systems = Array.isArray(b.systems)
    ? (b.systems as Array<{ slug?: unknown; name?: unknown }>).flatMap((x) => (typeof x.slug === 'string' && typeof x.name === 'string' ? [{ slug: x.slug, name: x.name }] : []))
    : null;
  return { own: { slug: b.own.slug, name: typeof b.own.name === 'string' ? b.own.name : null, combat: b.own.combat === true }, systems, borrow: parseBorrow(b.borrow) };
}

const field = 'rounded bg-stone-800 px-1 py-0.5 text-xs text-stone-100';

export function CombatRulesSetting({ fetchImpl }: { fetchImpl?: typeof fetch }) {
  const { campaignId } = useShell();
  const { role, rules, systemRules, act, setNotice } = useTableState();
  const [choices, setChoices] = useState<SystemChoices | null | 'asking'>('asking');
  // The systems to borrow from are a DM-only route (`ROUTE_POWER`): the setting is the DM's own (issues 1159, 1160).
  const isDm = useHolds()('dm') && role === 'dm';
  useEffect(() => {
    if (!isDm) return undefined;
    let live = true;
    void fetchSystemChoices(campaignId, fetchImpl).then((c) => { if (live) setChoices(c); });
    return () => { live = false; };
  }, [campaignId, isDm, fetchImpl]);
  if (!isDm) return null;
  const borrow = rules.borrow ?? null;
  const ownLabel = choices && choices !== 'asking' && !choices.own.combat ? "This system's (none)" : "This system's";
  const others = choices && choices !== 'asking' ? choices.systems ?? [] : [];
  // The one borrowed now is offered even when the list could not be read.
  const options = borrow && !others.some((s) => s.slug === borrow.system) ? [{ slug: borrow.system, name: borrow.name }, ...others] : others;
  const choose = (slug: string) => {
    const picked = options.find((s) => s.slug === slug);
    const next = picked ? { system: picked.slug, name: picked.name } : null;
    act({ kind: 'table.rules', rules: { ...rules, borrow: next } } as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That could not be set.'); });
  };
  return (
    <label className="flex flex-wrap items-center gap-1 text-xs" data-testid="combat-rules"
      title="The rules the table resolves attacks, initiative and areas by. Another system's apply at this table only, and every result they decide names them.">
      <span className="text-stone-400">Combat rules:</span>
      <select aria-label="Combat rules" className={field} value={borrow?.system ?? ''} onChange={(e) => choose(e.target.value)}>
        <option value="">{ownLabel}</option>
        {options.map((s) => <option key={s.slug} value={s.slug}>{`Borrow ${s.name}'s`}</option>)}
      </select>
      {choices === null && <span className="text-stone-500">The systems you can read could not be listed just now.</span>}
      {/* The borrowed system cannot be read now (bug check of #1320): the table plays by its own meanwhile. The DM's alone. */}
      {systemRules.borrowUnavailable && (
        <span role="status" className="text-amber-300" data-testid="borrow-unavailable">
          {`${systemRules.borrowUnavailable}'s rules cannot be read just now, so the table plays by this system's own rules.`}
        </span>
      )}
    </label>
  );
}

/**
 * "The DM may override a rule's outcome" (issue 1499; docs/MRCS/workbench.md §5.2, Tom): off by default. On, an attack's
 * outcome in the log offers the DM "Change the outcome". The table's rules setting, the DM's alone, as the borrow is.
 */
export function OverrideSetting() {
  const { role, rules, act, setNotice } = useTableState();
  const isDm = useHolds()('dm') && role === 'dm';
  if (!isDm) return null;
  return (
    <label className="flex items-center gap-1 text-xs" data-testid="override-setting"
      title="With this on, every outcome the rules decide offers you Change the outcome in the log. Players see only the changed result.">
      <input type="checkbox" aria-label="The DM may override a rule's outcome" checked={rules.override === true}
        onChange={(e) => act({ kind: 'table.rules', rules: { ...rules, override: e.target.checked } } as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That could not be set.'); })} />
      The DM may override a rule's outcome
    </label>
  );
}
